"use client";

import { TimeFrame } from "charts/src/types/chart";

import { ChartDateOptionButtonGroup } from "@/features/analysis/components/key-metrices-charts/components/ChartDateOptionButtonGroup";
import { ChartGridLayout } from "@/features/analysis/components/key-metrices-charts/components/ChartGridLayout";
import { ChartTimeFrameButtonGroup } from "@/features/analysis/components/key-metrices-charts/components/ChartTimeFrameButtonGroup";
import { getDefaultDateOption } from "@/features/analysis/components/key-metrices-charts/data/date-options";
import { useTimeFrameParams } from "@/features/analysis/hooks/use-time-frame-params";
import { useUpdateQuery } from "@/hooks/use-update-query";
import { Lang } from "@/i18n-config";
import { getTranslations } from "@/utils/i18n/get-translations";

type Props = {
  lang: Lang;
  symbol: string;
  isBlocked: boolean;
};

export const ChartGridView = ({ lang, symbol, isBlocked }: Props) => {
  const { t } = getTranslations(lang);
  const { updateQueries } = useUpdateQuery();
  const { timeFrame, dateOption } = useTimeFrameParams();

  const handleTimeFrameChange = (value: string) => {
    const defaultDateOption = getDefaultDateOption(value as TimeFrame);
    updateQueries([
      { name: "timeFrame", value },
      { name: "dateOption", value: defaultDateOption },
    ]);
  };

  return (
    <>
      <div className="relative mb-4 flex flex-col items-start gap-4 lg:items-center">
        <ChartTimeFrameButtonGroup
          lang={lang}
          type="financialReport"
          value={timeFrame}
          onButtonClick={handleTimeFrameChange}
        />
        <div className="lg:absolute lg:right-0 lg:top-1/2 lg:-translate-y-1/2">
          <ChartDateOptionButtonGroup dateOption={dateOption} />
        </div>
      </div>
      <div className="flex flex-col gap-7">
        <div>
          <ChartGridLayout
            title={t.analysis.financialReports.financial}
            symbol={symbol}
            lang={lang}
            timeFrame={timeFrame}
            dateOption={dateOption}
            chartCategoryPath="financialReport.financial"
            isBlocked={isBlocked}
          />
        </div>
        <div>
          <ChartGridLayout
            title={t.analysis.financialReports.valuation}
            symbol={symbol}
            lang={lang}
            timeFrame={timeFrame}
            dateOption={dateOption}
            chartCategoryPath="financialReport.valuation"
            isBlocked={isBlocked}
          />
        </div>
        <div>
          <ChartGridLayout
            title={t.analysis.financialReports.growth}
            symbol={symbol}
            lang={lang}
            timeFrame={timeFrame}
            dateOption={dateOption}
            chartCategoryPath="financialReport.growth"
            isBlocked={isBlocked}
          />
        </div>
        <div>
          <ChartGridLayout
            title={t.analysis.financialReports.profitability}
            symbol={symbol}
            lang={lang}
            timeFrame={timeFrame}
            dateOption={dateOption}
            chartCategoryPath="financialReport.profitability"
            isBlocked={isBlocked}
          />
        </div>
      </div>
    </>
  );
};

export default ChartGridView;
